import { useQueryClient } from '@tanstack/react-query';
import { DomainApiModel } from '../../../../types/models/domain.model';
import { CreateDomainApiRequest } from '../../../../types/requests/domains/create-domain.request';
import { DeleteDomainApiRequest } from '../../../../types/requests/domains/delete-domain.request';
import { GetDomainApiRequest } from '../../../../types/requests/domains/get-domain.request';
import { SearchDomainsApiRequest } from '../../../../types/requests/domains/search-domains.request';
import { UpdateDomainApiRequest } from '../../../../types/requests/domains/update-domain.request';
import { createDomain, deleteDomain, getDomain, searchDomains, updateDomain } from '../../api/domains.api';
import { useAxiosInfiniteQuery, useAxiosMutation, useAxiosQuery } from './use-axios.hook';

const baseQueryKey = 'domain';
const searchQueryKey = 'search';

export const useCreateDomain = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: CreateDomainApiRequest) => (await createDomain(params)).data,
    onSuccess: () =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => queryKey[0] === baseQueryKey && queryKey[1] === searchQueryKey,
      }),
  });
  return [request, { ...result, domain: data }] as const;
};

export const useUpdateDomain = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: UpdateDomainApiRequest) => (await updateDomain(params)).data,
    onSuccess: (domain) =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) =>
          queryKey[0] === baseQueryKey &&
          (queryKey[1] === searchQueryKey || (queryKey[1] as GetDomainApiRequest)?.domainId === domain.id),
      }),
  });
  return [request, { ...result, domain: data }] as const;
};

export const useGetDomain = <T extends DomainApiModel>(params: GetDomainApiRequest) => {
  const { data, ...result } = useAxiosQuery<T>({
    queryKey: [baseQueryKey, params],
    queryFn: async () => (await getDomain<T>(params)).data,
    enabled: Boolean(params.domainId && !Number.isNaN(params.domainId)),
  });
  return { ...result, domain: data } as const;
};

export const useSearchDomains = (params: SearchDomainsApiRequest, enabled?: boolean) => {
  const { data, ...result } = useAxiosInfiniteQuery({
    queryKey: [baseQueryKey, searchQueryKey, params],
    queryFn: async ({ pageParam }) => (await searchDomains({ cursor: pageParam, ...params })).data,
    initialPageParam: params.cursor,
    getNextPageParam: (lastResponse) => lastResponse.nextCursor,
    enabled,
  });
  return { ...result, response: data } as const;
};

export const useDeleteDomain = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: DeleteDomainApiRequest) => (await deleteDomain(params)).data,
    onSuccess: () =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => queryKey[0] === baseQueryKey && queryKey[1] === searchQueryKey,
      }),
  });
  return [request, result] as const;
};
